import { createContext, createElement, useContext } from 'react';
import it from './data/i18n/it.json';
import ru from './data/i18n/ru.json';
import uk from './data/i18n/uk.json';
import en from './data/i18n/en.json';
import ro from './data/i18n/ro.json';

// Le lingue dell'app del cliente. L'italiano è la lingua principale: quello che manca nelle altre torna in italiano,
// chiave per chiave (come backend/notifiche.py con notifiche_testi/{lingua}.json). Le lingue arrivano a pezzi e
// l'app non deve mai mostrare "undefined" o un buco.
//
// La lingua NON sta nell'indirizzo: è quella del cliente (campo `lingua` del suo profilo, "it" di base).
// Lo staff (admin e collaboratori) resta sempre in italiano.
//
// Come si usa
// - in un componente:  const { t, tp, valuta } = useTraduzioni();  t('comune.salva')
// - fuori (utils/…):   traduci(lingua, 'comune.salva')   oppure   const { t } = contesto(lingua)
//
// nome: come si chiama la lingua in quella lingua; bcp: per date, numeri e plurali (Intl).
export const LINGUE = [
  { codice: 'it', nome: 'Italiano', bcp: 'it-IT' },
  { codice: 'ru', nome: 'Русский', bcp: 'ru-RU' },
  { codice: 'uk', nome: 'Українська', bcp: 'uk-UA' },
  { codice: 'en', nome: 'English', bcp: 'en-GB' },
  { codice: 'ro', nome: 'Română', bcp: 'ro-RO' },
];
export const PRINCIPALE = 'it';
export const CODICI = LINGUE.map((l) => l.codice);
const DIZIONARI = { it, ru, uk, en, ro };
const BCP = Object.fromEntries(LINGUE.map((l) => [l.codice, l.bcp]));

// Le forme di plurale che ogni lingua deve avere nei dizionari (Intl.PluralRules per i numeri interi di tutti i
// giorni: 1, 2, 5, 21…). L'italiano e l'inglese ne hanno due, il russo e l'ucraino quattro, il rumeno tre.
export const FORME_PLURALE = {
  it: ['one', 'other'],
  ru: ['one', 'few', 'many', 'other'],
  uk: ['one', 'few', 'many', 'other'],
  en: ['one', 'other'],
  ro: ['one', 'few', 'other'],
};

const FUSO = 'Europe/Rome'; // le date dell'app sono sempre nell'ora di Roma (come utils/percorso.js)
const OPZIONI_DATA = { day: 'numeric', month: 'long', year: 'numeric' };
const OPZIONI_ORA = { hour: '2-digit', minute: '2-digit' };

// Una lingua che conosciamo, altrimenti l'italiano (profilo vuoto, codice sbagliato, cliente vecchio)
export const valida = (codice) => (DIZIONARI[codice] ? codice : PRINCIPALE);
export const bcpDi = (codice) => BCP[valida(codice)];
export const dizionario = (codice) => DIZIONARI[valida(codice)];

// La lingua di chi sta usando l'app: solo il cliente ha la sua, lo staff lavora sempre in italiano
export const linguaDelUtente = (userType, utente) => (userType === 'cliente' ? valida(utente?.lingua) : PRINCIPALE);

// "Mancano {quanti} documenti" + { quanti: 3 } → "Mancano 3 documenti"
export const compila = (testo, valori) => String(testo).replace(/\{(\w+)\}/g, (_, k) => {
  const v = valori?.[k];
  return v === undefined || v === null ? '' : String(v);
});

const pieno = (v) => v !== undefined && v !== null && v !== '';
// "immobili.titolo" → il valore dentro il dizionario (undefined se il ramo non c'è)
const dentro = (diz, chiave) => String(chiave).split('.').reduce((o, p) => (o == null ? undefined : o[p]), diz);

// Il valore grezzo con il ripiego per chiave: se nella lingua manca (o è ancora vuoto) vale l'italiano.
// Torna anche da quale lingua arriva il testo: serve ai plurali (le forme sono quelle della lingua del testo).
export const voce = (codice, chiave) => {
  const l = valida(codice);
  const mio = dentro(DIZIONARI[l], chiave);
  if (pieno(mio)) return { valore: mio, da: l };
  const ita = dentro(DIZIONARI[PRINCIPALE], chiave);
  return pieno(ita) ? { valore: ita, da: PRINCIPALE } : { valore: undefined, da: null };
};

const avvisa = (chiave) => {
  if (process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'test') {
    // eslint-disable-next-line no-console
    console.warn(`i18n: chiave senza testo "${chiave}"`);
  }
};

// Il testo nella lingua, con i segnaposto già riempiti. Se la chiave non c'è nemmeno in italiano torna la chiave
// stessa (mai "undefined" a video): il test di parità serve proprio a non farlo succedere.
export const traduci = (codice, chiave, valori) => {
  const { valore } = voce(codice, chiave);
  if (valore === undefined) {
    avvisa(chiave);
    return String(chiave);
  }
  if (typeof valore !== 'string') return valore; // elenchi e gruppi (per esempio i plurali)
  return valori ? compila(valore, valori) : valore;
};

const REGOLE = {};
const regolaPlurale = (bcp) => {
  if (!REGOLE[bcp]) REGOLE[bcp] = new Intl.PluralRules(bcp);
  return REGOLE[bcp];
};

// Un gruppo di plurali serve solo se ha almeno una forma scritta: i file non ancora tradotti hanno le forme vuote
const gruppoUtile = (v) => v && typeof v === 'object' && !Array.isArray(v) && Object.values(v).some(pieno);

// Plurale vero: la chiave punta a un gruppo { one, few, many, other } e {n} è sempre disponibile nel testo.
// La forma si sceglie con le regole della lingua da cui arriva il testo: se il russo è ancora vuoto si usa
// l'italiano con le regole italiane (altrimenti "5" prenderebbe la forma russa "many" di un testo italiano).
export const plurale = (codice, chiave, n, valori) => {
  const l = valida(codice);
  const mio = dentro(DIZIONARI[l], chiave);
  const gruppo = gruppoUtile(mio) ? { valore: mio, da: l } : { valore: dentro(DIZIONARI[PRINCIPALE], chiave), da: PRINCIPALE };
  if (!gruppoUtile(gruppo.valore)) {
    avvisa(chiave);
    return String(n);
  }
  const forma = regolaPlurale(BCP[gruppo.da]).select(n);
  const testo = pieno(gruppo.valore[forma]) ? gruppo.valore[forma] : gruppo.valore.other;
  if (!pieno(testo)) {
    avvisa(`${chiave}.${forma}`);
    return String(n);
  }
  return compila(testo, { n, ...valori });
};

const FORMATTATORI = {};
const formattatore = (tipo, bcp, opzioni) => {
  const chiave = `${tipo}|${bcp}|${JSON.stringify(opzioni)}`;
  if (!FORMATTATORI[chiave]) {
    FORMATTATORI[chiave] = tipo === 'data' ? new Intl.DateTimeFormat(bcp, opzioni) : new Intl.NumberFormat(bcp, opzioni);
  }
  return FORMATTATORI[chiave];
};

// new Date(null) darebbe il 1° gennaio 1970: una data che non c'è resta vuota
const aData = (valore) => {
  if (valore === null || valore === undefined || valore === '') return null;
  const d = valore instanceof Date ? valore : new Date(valore);
  return !isNaN(d.getTime()) ? d : null;
};

// La data nel formato della lingua, nell'ora di Roma: "18 settembre 2026", "18 сентября 2026 г.".
// Senza opzioni vale il formato lungo qui sopra; con le opzioni valgono solo quelle (OPZIONI_DATA non si somma).
export const dataIn = (codice, valore, opzioni) => {
  const d = aData(valore);
  if (!d) return '';
  const o = { timeZone: FUSO, ...(opzioni || OPZIONI_DATA) };
  try { return formattatore('data', bcpDi(codice), o).format(d); } catch { return d.toLocaleString(bcpDi(codice), o); }
};

// Solo l'ora: "09:12"
export const oraIn = (codice, valore) => dataIn(codice, valore, OPZIONI_ORA);
// Data e ora: "18 settembre 2026, 09:12"
export const dataOraIn = (codice, valore) => dataIn(codice, valore, { ...OPZIONI_DATA, ...OPZIONI_ORA });

// Numeri e prezzi nel formato della lingua: 225.000 in italiano, 225 000 in russo
export const numeroIn = (codice, n, opzioni) => (typeof n === 'number' && isFinite(n)
  ? formattatore('numero', bcpDi(codice), opzioni || {}).format(n) : '');
export const valutaIn = (codice, n, opzioni) => numeroIn(codice, n, { style: 'currency', currency: 'EUR', maximumFractionDigits: 0, ...opzioni });

// Messaggi di errore del server: il backend risponde sempre in italiano (detail) e non si tocca. Qui la chiave è
// proprio il testo italiano: se la traduzione c'è si mostra quella, altrimenti resta l'italiano com'è arrivato.
// Attenzione: le chiavi di questa sezione contengono punti e spazi, quindi si leggono di fila (niente "errori.xxx").
export const erroreIn = (codice, testo) => {
  if (typeof testo !== 'string' || !testo.trim()) return testo;
  const tradotto = dizionario(codice).errori?.[testo.trim()];
  return pieno(tradotto) ? tradotto : testo;
};

// Tutto quello che serve, anche fuori dai componenti (utils/…): const { t, tp, data, valuta } = contesto(lingua);
const CONTESTI = {};
export function contesto(codice) {
  const l = valida(codice);
  if (!CONTESTI[l]) {
    CONTESTI[l] = {
      l,
      bcp: BCP[l],
      t: (chiave, valori) => traduci(l, chiave, valori),
      tp: (chiave, n, valori) => plurale(l, chiave, n, valori),
      data: (valore, opzioni) => dataIn(l, valore, opzioni),
      ora: (valore) => oraIn(l, valore),
      dataOra: (valore) => dataOraIn(l, valore),
      numero: (n, opzioni) => numeroIn(l, n, opzioni),
      valuta: (n, opzioni) => valutaIn(l, n, opzioni),
      errore: (testo) => erroreIn(l, testo),
    };
  }
  return CONTESTI[l];
}

// ---------------------------------------------------------------- dentro React
// La lingua arriva dall'utente collegato (AuthContext) e la mette qui LayoutCliente, che avvolge le pagine del
// cliente. Fuori (login, pagine dello staff) vale l'italiano.
export const ContestoLingua = createContext(PRINCIPALE);
export const FornitoreLingua = ({ lingua, children }) => createElement(ContestoLingua.Provider, { value: valida(lingua) }, children);
export const useLingua = () => useContext(ContestoLingua);
export const useTraduzioni = () => contesto(useContext(ContestoLingua));
